/**
 * Buyer-Demand Phase 4 — admin quota builder.
 *
 * Two modes:
 *   - simple  (Phase 3 — retained): cell-by-cell editor. Each cell has
 *             its own constraint rows and target. Right for 1–2 cell
 *             briefs (male/female split).
 *   - nested  (Phase 4 — new): dimension-based Cartesian product
 *             generator. Admin defines dimensions (attribute + selected
 *             values); the builder auto-generates every combination as
 *             a cell. Right for 3+ cell studies. A 3×2 brief is a
 *             3-click setup instead of six manual cells.
 *
 * Both modes produce the same `QuotaCell[]` output. The backend, API,
 * and `quota_fill` table are unchanged. Mode is local component state,
 * inferred on mount from the existing cells; not saved to the server.
 *
 * Helpers (cartesianProduct / distributeEqually / inferQuotaMode) are
 * inlined below — they exist server-side at apps/api/src/lib/quotaUtils.ts
 * but the frontend can't import from apps/api. Logic and behaviour
 * mirror the server exactly so admin-time and complete-time generation
 * agree on every quota_key.
 *
 * See docs/investigations/buyer-demand-phase4-prep-2026-05-07.md.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, GitBranch, Layers } from 'lucide-react';
import {
  fetchCanonicalAttributes,
  fetchFeasibility,
  type AttrQualification,
  type CanonicalAttributeView,
  type ManualQualEntry,
  type QuotaCell,
} from '../lib/adminApi';

// ── Helpers (mirror apps/api/src/lib/quotaUtils.ts; cannot cross-import) ────

/**
 * Same algorithm as `apps/api/src/lib/quotaUtils.ts:buildQuotaKey`. Sort
 * order is the critical invariant — admin-time and complete-time
 * generation must collide on the same string. Server-side regression
 * tests pin the contract.
 */
export function buildQuotaKey(constraints: Record<string, string>): string {
  return Object.keys(constraints)
    .sort()
    .map((k) => `${k}:${constraints[k]}`)
    .join('|');
}

interface Dimension {
  attributeKey: string;
  values: string[];
}

function cartesianProduct(dimensions: Dimension[]): QuotaCell[] {
  if (dimensions.length === 0) return [];
  const filtered = dimensions.filter((d) => d.attributeKey !== '' && d.values.length > 0);
  if (filtered.length === 0) return [];
  const combos = filtered.reduce<Record<string, string>[]>(
    (acc, dim) =>
      acc.flatMap((combo) =>
        dim.values.map((v) => ({ ...combo, [dim.attributeKey]: v })),
      ),
    [{}],
  );
  return combos.map((constraints) => ({
    quota_key: buildQuotaKey(constraints),
    constraints,
    target: 0,
  }));
}

function distributeEqually(cells: QuotaCell[], totalTarget: number): QuotaCell[] {
  if (cells.length === 0) return [];
  const total = Math.max(0, Math.floor(totalTarget));
  const base = Math.floor(total / cells.length);
  const remainder = total % cells.length;
  return cells.map((cell, i) => ({
    ...cell,
    target: Math.max(1, base + (i < remainder ? 1 : 0)),
  }));
}

function inferQuotaMode(cells: QuotaCell[]): 'simple' | 'nested' {
  if (cells.length <= 1) return 'simple';
  const firstKeys = Object.keys(cells[0].constraints).sort();
  if (firstKeys.length === 0) return 'simple';
  const allSameKeys = cells.every(
    (c) => Object.keys(c.constraints).sort().join('|') === firstKeys.join('|'),
  );
  if (!allSameKeys) return 'simple';
  if (firstKeys.length === 1) return 'simple';

  const valueSets: Record<string, Set<string>> = {};
  for (const k of firstKeys) valueSets[k] = new Set();
  for (const cell of cells) {
    for (const k of firstKeys) valueSets[k].add(cell.constraints[k]);
  }
  const expectedCount = firstKeys.reduce((acc, k) => acc * valueSets[k].size, 1);
  if (expectedCount !== cells.length) return 'simple';

  const dims: Dimension[] = firstKeys.map((k) => ({
    attributeKey: k,
    values: [...valueSets[k]],
  }));
  const expected = new Set(cartesianProduct(dims).map((c) => c.quota_key));
  for (const k of expected) {
    if (!cells.some((c) => c.quota_key === k)) return 'simple';
  }
  return 'nested';
}

/**
 * Reverse of cartesianProduct — only safe when inferQuotaMode has
 * confirmed the cells form a complete product. Used to seed
 * dimensions state on mount when cells were saved in nested mode.
 */
function inferDimensions(cells: QuotaCell[]): Dimension[] {
  if (cells.length === 0) return [];
  const keys = Object.keys(cells[0].constraints).sort();
  return keys.map((attributeKey) => {
    const valueSet = new Set<string>();
    for (const c of cells) valueSet.add(c.constraints[attributeKey]);
    return { attributeKey, values: [...valueSet] };
  });
}

/**
 * Curated age brackets exposed in the quota builder. Format must match
 * the strings the server's `parseAgeRangeValue` accepts.
 */
const AGE_RANGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '18-24', label: '18–24' },
  { value: '18-34', label: '18–34' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '35-54', label: '35–54' },
  { value: '45-54', label: '45–54' },
  { value: '55+', label: '55+' },
  { value: '65+', label: '65+' },
];

// ── Component props ────────────────────────────────────────────────────────

interface Props {
  value: QuotaCell[];
  onChange: (cells: QuotaCell[]) => void;
  disabled?: boolean;
  /**
   * Optional. When provided, the nested-mode generated-cells table
   * shows a per-cell feasibility column (panel match count + green/
   * amber/red health indicator). Currently only used as a render flag —
   * the feasibility endpoint is project-agnostic, so the surveyId
   * itself is not sent to the API; callers can pass any truthy value
   * (the project's id is the natural choice).
   */
  surveyId?: string;
}

export default function QuotaBuilder({ value, onChange, disabled = false, surveyId }: Props) {
  const { data, isLoading: attrsLoading } = useQuery({
    queryKey: ['admin', 'canonical-attributes'],
    queryFn: fetchCanonicalAttributes,
  });

  // Canonical attribute list with age_range injected as a synthetic
  // option (same as Phase 3). Filtering excludes deprecated + derived.
  const attributes = useMemo<CanonicalAttributeView[]>(() => {
    const all = (data?.attributes ?? []).filter((a) => !a.deprecated && !a.derived);
    const hasDob = all.some((a) => a.key === 'date_of_birth');
    if (!hasDob) return all;
    const ageRangeView: CanonicalAttributeView = {
      key: 'age_range',
      label: 'Age range (derived from DOB)',
      category: 'demographics',
      inputType: 'single_select',
      options: AGE_RANGE_OPTIONS,
      memberCount: 0,
      b2b: false,
      derived: false,
      deprecated: false,
    };
    return [ageRangeView, ...all];
  }, [data]);

  const attrLookup = useMemo(() => {
    const m = new Map<string, CanonicalAttributeView>();
    for (const a of attributes) m.set(a.key, a);
    return m;
  }, [attributes]);

  // ── Mode + total target + dimensions state ────────────────────────────────

  const [mode, setMode] = useState<'simple' | 'nested'>(() => inferQuotaMode(value));
  const [totalTarget, setTotalTarget] = useState<number>(() => {
    const sum = value.reduce((s, c) => s + c.target, 0);
    return sum > 0 ? sum : 100;
  });
  const [dimensions, setDimensions] = useState<Dimension[]>(() =>
    inferQuotaMode(value) === 'nested' ? inferDimensions(value) : [],
  );

  // Track whether the user has hand-edited mode/dimensions so we don't
  // clobber them on later parent re-renders. (`value` may change from
  // outside the builder — e.g. distributeEqually — without us wanting
  // to re-infer.)
  const userTouchedRef = useRef(false);

  // Regenerate cells whenever dimensions change in nested mode. Preserve
  // existing targets keyed by quota_key (renaming a value gives a new
  // key, which correctly resets that cell to target=0).
  useEffect(() => {
    if (mode !== 'nested') return;
    if (!userTouchedRef.current) return; // no-op on initial mount
    const generated = cartesianProduct(dimensions);
    const targetsByKey = new Map(value.map((c) => [c.quota_key, c.target]));
    const next = generated.map((c) => ({
      ...c,
      target: targetsByKey.get(c.quota_key) ?? 0,
    }));
    onChange(next);
    // We deliberately do NOT depend on `value` or `onChange` — only on
    // dimensions. The parent owns `value`; recomputing here would
    // create an infinite render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, mode]);

  function switchMode(next: 'simple' | 'nested'): void {
    if (next === mode || disabled) return;
    if (next === 'nested') {
      // If existing cells form a Cartesian product, restore those
      // dimensions; otherwise start with one empty placeholder so the
      // admin has somewhere to type.
      const seed: Dimension[] =
        inferQuotaMode(value) === 'nested'
          ? inferDimensions(value)
          : [{ attributeKey: '', values: [] }];
      setDimensions(seed);
      // Don't trigger the regeneration effect — switching with an empty
      // placeholder dim should not yet erase existing cells. The first
      // real edit flips userTouchedRef and regeneration fires.
    }
    setMode(next);
  }

  function handleDistributeEqually(): void {
    if (disabled) return;
    onChange(distributeEqually(value, totalTarget));
  }

  // ── Overlap detection (same as Phase 3) ───────────────────────────────────
  const overlapKeys = useMemo(() => {
    const seen = new Map<string, number>();
    const dupes = new Set<string>();
    for (const cell of value) {
      const count = seen.get(cell.quota_key) ?? 0;
      if (count >= 1) dupes.add(cell.quota_key);
      seen.set(cell.quota_key, count + 1);
    }
    return dupes;
  }, [value]);

  // ── Simple-mode mutation helpers (Phase 3 behaviour preserved) ───────────

  function updateCell(idx: number, mutate: (cell: QuotaCell) => QuotaCell): void {
    onChange(value.map((c, i) => (i === idx ? mutate(c) : c)));
  }

  function addCell(): void {
    onChange([...value, { quota_key: '', constraints: {}, target: 50 }]);
  }

  function removeCell(idx: number): void {
    onChange(value.filter((_, i) => i !== idx));
  }

  function addConstraint(idx: number, attrKey: string, attrValue: string): void {
    updateCell(idx, (c) => {
      const constraints = { ...c.constraints, [attrKey]: attrValue };
      return { ...c, constraints, quota_key: buildQuotaKey(constraints) };
    });
  }

  function changeConstraintValue(idx: number, attrKey: string, attrValue: string): void {
    updateCell(idx, (c) => {
      const constraints = { ...c.constraints, [attrKey]: attrValue };
      return { ...c, constraints, quota_key: buildQuotaKey(constraints) };
    });
  }

  function changeConstraintAttr(idx: number, oldAttrKey: string, newAttrKey: string): void {
    if (oldAttrKey === newAttrKey) return;
    const newAttr = attrLookup.get(newAttrKey);
    const firstValue = newAttr?.options?.[0]?.value ?? '';
    updateCell(idx, (c) => {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(c.constraints)) {
        if (k === oldAttrKey) next[newAttrKey] = firstValue;
        else next[k] = v;
      }
      return { ...c, constraints: next, quota_key: buildQuotaKey(next) };
    });
  }

  function removeConstraint(idx: number, attrKey: string): void {
    updateCell(idx, (c) => {
      const next = { ...c.constraints };
      delete next[attrKey];
      return { ...c, constraints: next, quota_key: buildQuotaKey(next) };
    });
  }

  function changeTarget(idx: number, target: number): void {
    updateCell(idx, (c) => ({ ...c, target: Math.max(1, Math.floor(target) || 1) }));
  }

  // ── Nested-mode dimension mutation helpers ────────────────────────────────

  function updateDimension(idx: number, mutate: (dim: Dimension) => Dimension): void {
    userTouchedRef.current = true;
    setDimensions((prev) => prev.map((d, i) => (i === idx ? mutate(d) : d)));
  }

  function addDimension(): void {
    userTouchedRef.current = true;
    setDimensions((prev) => [...prev, { attributeKey: '', values: [] }]);
  }

  function removeDimension(idx: number): void {
    userTouchedRef.current = true;
    setDimensions((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  if (attrsLoading) {
    return <div className="text-sm text-slate-400">Loading attributes…</div>;
  }

  const totalCellTargets = value.reduce((s, c) => s + c.target, 0);

  return (
    <div className="space-y-4">
      <BuilderHeader
        mode={mode}
        onModeChange={switchMode}
        totalTarget={totalTarget}
        onTotalTargetChange={setTotalTarget}
        onDistributeEqually={handleDistributeEqually}
        canDistribute={value.length > 0}
        disabled={disabled}
      />

      {mode === 'simple' ? (
        <SimpleMode
          value={value}
          attributes={attributes}
          attrLookup={attrLookup}
          overlapKeys={overlapKeys}
          totalCellTargets={totalCellTargets}
          disabled={disabled}
          onAddCell={addCell}
          onRemoveCell={removeCell}
          onAddConstraint={addConstraint}
          onChangeAttr={changeConstraintAttr}
          onChangeValue={changeConstraintValue}
          onRemoveConstraint={removeConstraint}
          onChangeTarget={changeTarget}
        />
      ) : (
        <NestedMode
          value={value}
          dimensions={dimensions}
          attributes={attributes}
          attrLookup={attrLookup}
          totalCellTargets={totalCellTargets}
          surveyId={surveyId}
          disabled={disabled}
          onAddDimension={addDimension}
          onRemoveDimension={removeDimension}
          onUpdateDimension={updateDimension}
          onChangeCellTarget={(quotaKey, target) =>
            onChange(
              value.map((c) =>
                c.quota_key === quotaKey
                  ? { ...c, target: Math.max(1, Math.floor(target) || 1) }
                  : c,
              ),
            )
          }
        />
      )}

      {overlapKeys.size > 0 && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            Two or more cells produce the same quota_key. The first-match-wins
            matcher will only ever fill the earlier one. Adjust the constraints
            to make cells non-overlapping.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Header (mode switcher + total target + distribute) ──────────────────────

function BuilderHeader({
  mode,
  onModeChange,
  totalTarget,
  onTotalTargetChange,
  onDistributeEqually,
  canDistribute,
  disabled,
}: {
  mode: 'simple' | 'nested';
  onModeChange: (m: 'simple' | 'nested') => void;
  totalTarget: number;
  onTotalTargetChange: (n: number) => void;
  onDistributeEqually: () => void;
  canDistribute: boolean;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-white">Quotas</p>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">
            Each cell defines a target completion count for a slice of the panel.
            A member matches at most one cell — first match wins. Members whose
            attributes don't match any cell complete normally without quota
            accounting.
          </p>
        </div>
        <ModeSwitcher mode={mode} onModeChange={onModeChange} disabled={disabled} />
      </div>

      <div className="flex items-center gap-3 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
        <label className="text-xs uppercase tracking-wide text-slate-400">Total target</label>
        <input
          type="number"
          min={1}
          value={totalTarget}
          onChange={(e) => onTotalTargetChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
          disabled={disabled}
          className="w-28 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
        />
        <button
          onClick={onDistributeEqually}
          disabled={disabled || !canDistribute}
          className="text-xs bg-slate-700 text-white px-3 py-1.5 rounded hover:bg-slate-600 disabled:opacity-50"
        >
          Distribute equally
        </button>
        <span className="text-[11px] text-slate-500">
          (not saved — convenience input for splitting targets across cells)
        </span>
      </div>
    </div>
  );
}

function ModeSwitcher({
  mode,
  onModeChange,
  disabled,
}: {
  mode: 'simple' | 'nested';
  onModeChange: (m: 'simple' | 'nested') => void;
  disabled: boolean;
}) {
  return (
    <div className="inline-flex border border-slate-700 rounded-lg overflow-hidden text-xs">
      <button
        onClick={() => onModeChange('simple')}
        disabled={disabled}
        className={`px-3 py-1.5 inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${
          mode === 'simple' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
        }`}
      >
        <Layers className="w-3 h-3" /> Simple
      </button>
      <button
        onClick={() => onModeChange('nested')}
        disabled={disabled}
        className={`px-3 py-1.5 inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${
          mode === 'nested' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
        }`}
      >
        <GitBranch className="w-3 h-3" /> Nested
      </button>
    </div>
  );
}

// ── Simple mode (Phase 3 behaviour + percentage column) ─────────────────────

interface SimpleModeProps {
  value: QuotaCell[];
  attributes: CanonicalAttributeView[];
  attrLookup: Map<string, CanonicalAttributeView>;
  overlapKeys: Set<string>;
  totalCellTargets: number;
  disabled: boolean;
  onAddCell: () => void;
  onRemoveCell: (idx: number) => void;
  onAddConstraint: (idx: number, attrKey: string, attrValue: string) => void;
  onChangeAttr: (idx: number, oldKey: string, newKey: string) => void;
  onChangeValue: (idx: number, attrKey: string, attrValue: string) => void;
  onRemoveConstraint: (idx: number, attrKey: string) => void;
  onChangeTarget: (idx: number, target: number) => void;
}

function SimpleMode(props: SimpleModeProps) {
  const {
    value, attributes, attrLookup, overlapKeys, totalCellTargets, disabled,
    onAddCell, onRemoveCell, onAddConstraint, onChangeAttr, onChangeValue,
    onRemoveConstraint, onChangeTarget,
  } = props;

  return (
    <>
      {value.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          No quotas defined — every qualifying member completes regardless of cell.
        </p>
      ) : (
        <ul className="space-y-3">
          {value.map((cell, idx) => (
            <CellCard
              key={idx}
              cell={cell}
              attributes={attributes}
              attrLookup={attrLookup}
              isOverlap={cell.quota_key !== '' && overlapKeys.has(cell.quota_key)}
              totalCellTargets={totalCellTargets}
              disabled={disabled}
              onRemoveCell={() => onRemoveCell(idx)}
              onAddConstraint={(k, v) => onAddConstraint(idx, k, v)}
              onChangeAttr={(oldKey, newKey) => onChangeAttr(idx, oldKey, newKey)}
              onChangeValue={(k, v) => onChangeValue(idx, k, v)}
              onRemoveConstraint={(k) => onRemoveConstraint(idx, k)}
              onChangeTarget={(t) => onChangeTarget(idx, t)}
            />
          ))}
        </ul>
      )}

      <button
        onClick={onAddCell}
        disabled={disabled}
        className="inline-flex items-center gap-1 bg-primary text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 disabled:opacity-50"
      >
        <Plus className="w-4 h-4" /> Add cell
      </button>
    </>
  );
}

interface CellCardProps {
  cell: QuotaCell;
  attributes: CanonicalAttributeView[];
  attrLookup: Map<string, CanonicalAttributeView>;
  isOverlap: boolean;
  totalCellTargets: number;
  disabled: boolean;
  onRemoveCell: () => void;
  onAddConstraint: (attrKey: string, attrValue: string) => void;
  onChangeAttr: (oldAttrKey: string, newAttrKey: string) => void;
  onChangeValue: (attrKey: string, attrValue: string) => void;
  onRemoveConstraint: (attrKey: string) => void;
  onChangeTarget: (target: number) => void;
}

function CellCard(props: CellCardProps) {
  const {
    cell, attributes, attrLookup, isOverlap, totalCellTargets, disabled,
    onRemoveCell, onAddConstraint, onChangeAttr, onChangeValue, onRemoveConstraint, onChangeTarget,
  } = props;

  const usedKeys = new Set(Object.keys(cell.constraints));
  const availableForAdd = attributes.filter((a) => !usedKeys.has(a.key));
  const firstAvailable = availableForAdd[0];

  function handleAdd(): void {
    if (!firstAvailable) return;
    const initialValue = firstAvailable.options?.[0]?.value ?? '';
    onAddConstraint(firstAvailable.key, initialValue);
  }

  return (
    <li
      className={`bg-slate-900 rounded-lg p-4 border space-y-3 ${
        isOverlap ? 'border-amber-500/40' : 'border-slate-700'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-slate-300 break-all">
            {cell.quota_key || (
              <span className="italic text-slate-500">(empty cell — add at least one constraint)</span>
            )}
          </p>
        </div>
        <button
          onClick={onRemoveCell}
          disabled={disabled}
          className="text-slate-500 hover:text-red-400 disabled:opacity-50"
          aria-label="Remove cell"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        {Object.entries(cell.constraints).map(([attrKey, attrValue]) => (
          <ConstraintRow
            key={attrKey}
            attrKey={attrKey}
            attrValue={attrValue}
            attributes={attributes}
            attrLookup={attrLookup}
            usedKeys={usedKeys}
            disabled={disabled}
            onChangeAttr={(newAttrKey) => onChangeAttr(attrKey, newAttrKey)}
            onChangeValue={(v) => onChangeValue(attrKey, v)}
            onRemove={() => onRemoveConstraint(attrKey)}
          />
        ))}
        {firstAvailable && (
          <button
            onClick={handleAdd}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Add constraint
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-slate-700">
        <label className="text-xs uppercase tracking-wide text-slate-400">Target</label>
        <input
          type="number"
          min={1}
          value={cell.target}
          onChange={(e) => onChangeTarget(parseInt(e.target.value, 10))}
          disabled={disabled}
          className="w-24 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
        />
        <span className="text-xs text-slate-500">completes</span>
        <span className="text-xs text-slate-500 ml-auto tabular-nums">
          {formatPct(cell.target, totalCellTargets)}
        </span>
      </div>
    </li>
  );
}

function ConstraintRow({
  attrKey,
  attrValue,
  attributes,
  attrLookup,
  usedKeys,
  disabled,
  onChangeAttr,
  onChangeValue,
  onRemove,
}: {
  attrKey: string;
  attrValue: string;
  attributes: CanonicalAttributeView[];
  attrLookup: Map<string, CanonicalAttributeView>;
  usedKeys: Set<string>;
  disabled: boolean;
  onChangeAttr: (newAttrKey: string) => void;
  onChangeValue: (v: string) => void;
  onRemove: () => void;
}) {
  const attr = attrLookup.get(attrKey);
  const attrOptions = attributes.filter((a) => a.key === attrKey || !usedKeys.has(a.key));
  const valueOptions = attr?.options ?? [];
  const valueIsFreeText =
    attr && attr.key !== 'age_range' &&
    (attr.inputType === 'number' || attr.inputType === 'text' || attr.inputType === 'date') &&
    valueOptions.length === 0;

  return (
    <div className="flex items-center gap-2">
      <select
        value={attrKey}
        onChange={(e) => onChangeAttr(e.target.value)}
        disabled={disabled}
        className="flex-1 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
      >
        {attrOptions.map((a) => (
          <option key={a.key} value={a.key}>{a.label}</option>
        ))}
      </select>
      {valueIsFreeText ? (
        <input
          type="text"
          value={attrValue}
          onChange={(e) => onChangeValue(e.target.value)}
          disabled={disabled}
          placeholder="value"
          className="flex-1 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
        />
      ) : (
        <select
          value={attrValue}
          onChange={(e) => onChangeValue(e.target.value)}
          disabled={disabled}
          className="flex-1 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
        >
          {valueOptions.length === 0 && <option value="">—</option>}
          {valueOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
      <button
        onClick={onRemove}
        disabled={disabled}
        className="text-slate-500 hover:text-red-400 disabled:opacity-50"
        aria-label="Remove constraint"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Nested mode (Phase 4) ────────────────────────────────────────────────────

interface NestedModeProps {
  value: QuotaCell[];
  dimensions: Dimension[];
  attributes: CanonicalAttributeView[];
  attrLookup: Map<string, CanonicalAttributeView>;
  totalCellTargets: number;
  surveyId?: string;
  disabled: boolean;
  onAddDimension: () => void;
  onRemoveDimension: (idx: number) => void;
  onUpdateDimension: (idx: number, mutate: (dim: Dimension) => Dimension) => void;
  onChangeCellTarget: (quotaKey: string, target: number) => void;
}

function NestedMode(props: NestedModeProps) {
  const {
    value, dimensions, attributes, attrLookup, totalCellTargets, surveyId, disabled,
    onAddDimension, onRemoveDimension, onUpdateDimension, onChangeCellTarget,
  } = props;

  const usedDimKeys = useMemo(
    () => new Set(dimensions.map((d) => d.attributeKey).filter(Boolean)),
    [dimensions],
  );

  return (
    <div className="space-y-4">
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-white">Dimensions</p>
          <span className="text-xs text-slate-500">
            {dimensions.length === 0
              ? 'add a dimension to start generating cells'
              : `${dimensions.length} dimension${dimensions.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {dimensions.length > 0 && (
          <ul className="space-y-2">
            {dimensions.map((dim, idx) => (
              <DimensionRow
                key={idx}
                dim={dim}
                attributes={attributes}
                attrLookup={attrLookup}
                usedDimKeys={usedDimKeys}
                disabled={disabled}
                onRemove={() => onRemoveDimension(idx)}
                onUpdate={(mutate) => onUpdateDimension(idx, mutate)}
              />
            ))}
          </ul>
        )}

        <button
          onClick={onAddDimension}
          disabled={disabled}
          className="inline-flex items-center gap-1 bg-primary text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add dimension
        </button>
      </div>

      <GeneratedCellsTable
        cells={value}
        attrLookup={attrLookup}
        totalCellTargets={totalCellTargets}
        surveyId={surveyId}
        disabled={disabled}
        onChangeCellTarget={onChangeCellTarget}
      />
    </div>
  );
}

function DimensionRow({
  dim,
  attributes,
  attrLookup,
  usedDimKeys,
  disabled,
  onRemove,
  onUpdate,
}: {
  dim: Dimension;
  attributes: CanonicalAttributeView[];
  attrLookup: Map<string, CanonicalAttributeView>;
  usedDimKeys: Set<string>;
  disabled: boolean;
  onRemove: () => void;
  onUpdate: (mutate: (dim: Dimension) => Dimension) => void;
}) {
  const attr = dim.attributeKey ? attrLookup.get(dim.attributeKey) : undefined;
  const valueOptions = attr?.options ?? [];

  // The dropdown lists attributes not already used in *other* dimensions
  // of this builder, plus this row's own current attribute.
  const attrOptions = attributes.filter(
    (a) => a.key === dim.attributeKey || !usedDimKeys.has(a.key),
  );

  function changeAttr(newKey: string): void {
    onUpdate(() => ({ attributeKey: newKey, values: [] }));
  }

  function toggleValue(val: string): void {
    onUpdate((d) => {
      const has = d.values.includes(val);
      const next = has ? d.values.filter((v) => v !== val) : [...d.values, val];
      return { ...d, values: next };
    });
  }

  return (
    <li className="bg-slate-800 rounded-lg p-3 border border-slate-700 space-y-2">
      <div className="flex items-center gap-2">
        <select
          value={dim.attributeKey}
          onChange={(e) => changeAttr(e.target.value)}
          disabled={disabled}
          className="flex-1 bg-slate-900 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50"
        >
          {dim.attributeKey === '' && <option value="">— select attribute —</option>}
          {attrOptions.map((a) => (
            <option key={a.key} value={a.key}>{a.label}</option>
          ))}
        </select>
        <button
          onClick={onRemove}
          disabled={disabled}
          className="text-slate-500 hover:text-red-400 disabled:opacity-50"
          aria-label="Remove dimension"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {attr && valueOptions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {valueOptions.map((opt) => {
            const checked = dim.values.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleValue(opt.value)}
                disabled={disabled}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${
                  checked
                    ? 'bg-primary border-primary text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
      {attr && valueOptions.length === 0 && (
        <p className="text-xs text-slate-500 italic">
          This attribute has no canonical option list — use simple mode for free-text values.
        </p>
      )}
    </li>
  );
}

function GeneratedCellsTable({
  cells,
  attrLookup,
  totalCellTargets,
  surveyId,
  disabled,
  onChangeCellTarget,
}: {
  cells: QuotaCell[];
  attrLookup: Map<string, CanonicalAttributeView>;
  totalCellTargets: number;
  surveyId?: string;
  disabled: boolean;
  onChangeCellTarget: (quotaKey: string, target: number) => void;
}) {
  // ── Per-cell feasibility (debounced) ──────────────────────────────────────
  // Run only when surveyId is provided. Each cell becomes one fetchFeasibility
  // call (the existing endpoint is project-agnostic and aggregates panel
  // matches across the qual array; we treat each cell's constraints as a
  // standalone qual array). React Query dedupes identical key sets across
  // cells (e.g. {gender:female} only fetches once even if it appears in
  // multiple per-cell qual arrays — same query key).

  const [debouncedCells, setDebouncedCells] = useState<QuotaCell[]>(cells);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCells(cells), 500);
    return () => clearTimeout(t);
  }, [cells]);

  const feasibilityEnabled = !!surveyId && debouncedCells.length > 0;

  const feasibilityResults = useQueries({
    queries: debouncedCells.map((cell) => {
      const quals = cellToQuals(cell, attrLookup);
      return {
        queryKey: ['admin', 'cell-feasibility', JSON.stringify(quals)],
        queryFn: () => fetchFeasibility(quals),
        enabled: feasibilityEnabled,
        staleTime: 60_000,
      };
    }),
  });

  if (cells.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">
        Add at least one dimension with selected values to generate cells.
      </p>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-800 text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="text-left px-3 py-2">Constraints</th>
            <th className="text-right px-3 py-2 w-28">Target</th>
            <th className="text-right px-3 py-2 w-20">% of total</th>
            {surveyId && <th className="text-right px-3 py-2 w-32">Feasibility</th>}
          </tr>
        </thead>
        <tbody>
          {cells.map((cell, idx) => {
            const fres = feasibilityResults[idx];
            return (
              <tr key={cell.quota_key} className="border-t border-slate-700">
                <td className="px-3 py-2 text-white truncate" title={describeConstraints(cell.constraints)}>
                  {describeConstraints(cell.constraints) || (
                    <span className="text-slate-500 italic">(no constraints)</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    min={1}
                    value={cell.target}
                    onChange={(e) =>
                      onChangeCellTarget(cell.quota_key, parseInt(e.target.value, 10))
                    }
                    disabled={disabled}
                    className="w-24 bg-slate-800 text-white rounded px-2 py-1 text-sm border border-slate-700 disabled:opacity-50 text-right"
                  />
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-300">
                  {formatPct(cell.target, totalCellTargets)}
                </td>
                {surveyId && (
                  <td className="px-3 py-2 text-right">
                    <FeasibilityCell
                      result={fres}
                      target={cell.target}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FeasibilityCell({
  result,
  target,
}: {
  result: { data?: { panelMatch: number }; isLoading: boolean; isError: boolean } | undefined;
  target: number;
}) {
  if (!result || result.isLoading) {
    return <span className="text-xs text-slate-500">…</span>;
  }
  if (result.isError || !result.data) {
    return <span className="text-xs text-slate-500">—</span>;
  }
  const panelCount = result.data.panelMatch;
  let cls = 'text-slate-300';
  if (panelCount < target) cls = 'text-red-400 font-semibold';
  else if (panelCount < target * 2) cls = 'text-amber-400 font-semibold';
  else cls = 'text-green-400 font-semibold';

  return (
    <span className={`text-xs tabular-nums ${cls}`} title={`${panelCount.toLocaleString()} matching members in panel`}>
      {panelCount.toLocaleString()}
    </span>
  );
}

// ── Constraint-to-qual translator (for feasibility per cell) ────────────────

/**
 * Build the qualification array fetchFeasibility expects from a quota
 * cell's constraints. Match-kind selection mirrors the server-side
 * matcher's semantics:
 *   - age_range: dedicated kind, parses "18-34" / "55+" into bounds.
 *   - multi_select attributes: `multi_select` with require_any=true so
 *     the panel match counts members whose stored comma-joined value
 *     CONTAINS the constraint value (matches quotaUtils' contains
 *     check at complete time).
 *   - everything else: `choice` with values=[constraintValue].
 */
function cellToQuals(
  cell: QuotaCell,
  attrLookup: Map<string, CanonicalAttributeView>,
): ManualQualEntry[] {
  const out: AttrQualification[] = [];
  for (const [attrKey, value] of Object.entries(cell.constraints)) {
    if (attrKey === 'age_range') {
      const bounds = parseAgeRangeValue(value);
      if (bounds) {
        out.push({
          attributeKey: 'date_of_birth',
          match: { kind: 'age_range', minAge: bounds[0], maxAge: bounds[1] },
        });
      }
      continue;
    }
    const attr = attrLookup.get(attrKey);
    if (attr?.inputType === 'multi_select') {
      out.push({
        attributeKey: attrKey,
        match: { kind: 'multi_select', values: [value], require_any: true },
      });
    } else {
      out.push({
        attributeKey: attrKey,
        match: { kind: 'choice', values: [value] },
      });
    }
  }
  return out;
}

function parseAgeRangeValue(value: string): [number, number] | null {
  const trimmed = value.trim();
  const plus = /^(\d+)\+$/.exec(trimmed);
  if (plus) {
    const min = parseInt(plus[1], 10);
    return Number.isFinite(min) ? [min, 120] : null;
  }
  const range = /^(\d+)-(\d+)$/.exec(trimmed);
  if (range) {
    const min = parseInt(range[1], 10);
    const max = parseInt(range[2], 10);
    return Number.isFinite(min) && Number.isFinite(max) && min <= max ? [min, max] : null;
  }
  return null;
}

// ── Display helpers ────────────────────────────────────────────────────────

function formatPct(target: number, totalTargets: number): string {
  if (totalTargets <= 0) return '—';
  const pct = (target / totalTargets) * 100;
  return `${(Math.round(pct * 10) / 10).toFixed(1)}%`;
}

function describeConstraints(constraints: Record<string, string>): string {
  const keys = Object.keys(constraints).sort();
  if (keys.length === 0) return '';
  return keys.map((k) => `${humanize(k)}: ${constraints[k]}`).join(' · ');
}

function humanize(key: string): string {
  return key
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}
